import { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Card, CardActionArea, CardContent, Chip, CircularProgress, Grid, Stack, TextField, Typography,
} from '@mui/material';
import { CheckCircle, RadioButtonUnchecked, Search } from '@mui/icons-material';
import { brand, text } from '../../theme/colors';

/**
 * "Which of these is you?" — the conveyancer's own entry in the Law Society of
 * Zimbabwe directory. We search with their account name first; they can
 * search again with the name as it appears on their practising certificate,
 * and narrow by firm or town. Only entries allowed to practise as a
 * conveyancer this year are offered (the server filters).
 *
 * value: the linked practitioner id (or null); onChange(practitionerId, practitioner).
 */
export default function LszEntryPicker({ value, onChange, search }) {
  const [query, setQuery] = useState({ name: '', firm: '', town: '' });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const run = useCallback(async (params) => {
    setLoading(true); setError('');
    try {
      const res = await search(params);
      setResults(res.data || []);
    } catch {
      setError('Could not search the Law Society directory. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [search]);

  // First look: their own account name.
  useEffect(() => { run({}); }, [run]);

  const submit = (e) => {
    e.preventDefault();
    const params = {};
    if (query.name.trim().length >= 2) params.name = query.name.trim();
    if (query.firm.trim()) params.firm = query.firm.trim();
    if (query.town.trim()) params.town = query.town.trim();
    run(params);
  };

  return (
    <Box>
      <Typography sx={{ fontWeight: 600, mb: 0.5 }}>Your Law Society entry</Typography>
      <Typography variant="body2" sx={{ color: text.secondary, mb: 2 }}>
        Pick yourself in the Law Society of Zimbabwe&apos;s directory of practitioners. We check it every day — buyers see
        your good standing only while it lists you as allowed to practise as a conveyancer.
      </Typography>

      <Box component="form" onSubmit={submit} sx={{ mb: 2 }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField fullWidth size="small" label="Name as on your practising certificate" value={query.name}
              onChange={(e) => setQuery({ ...query, name: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField fullWidth size="small" label="Law firm (optional)" value={query.firm}
              onChange={(e) => setQuery({ ...query, firm: e.target.value })} />
          </Grid>
          <Grid item xs={8} md={2}>
            <TextField fullWidth size="small" label="Town (optional)" value={query.town}
              onChange={(e) => setQuery({ ...query, town: e.target.value })} />
          </Grid>
          <Grid item xs={4} md={2}>
            <Button fullWidth type="submit" variant="outlined" startIcon={<Search />} sx={{ height: 40 }}>Search</Button>
          </Grid>
        </Grid>
      </Box>

      {loading && <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={28} sx={{ color: brand.primary }} /></Box>}
      {error && <Typography color="error" variant="body2">{error}</Typography>}
      {!loading && results && results.length === 0 && (
        <Typography variant="body2" sx={{ color: text.secondary }}>
          No conveyancer with that name in the current directory. Try the name exactly as it appears on your practising
          certificate. Admitted recently? Upload your certificate on the Documents page and our team will verify you by hand.
        </Typography>
      )}
      {!loading && results && results.length > 0 && (
        <Stack spacing={1}>
          {results.map(({ practitioner: p, takenByAnotherAccount }) => {
            const selected = value === p.id;
            return (
              <Card key={p.id} variant="outlined" sx={{ borderColor: selected ? brand.primary : undefined, borderWidth: selected ? 2 : 1 }}>
                <CardActionArea disabled={takenByAnotherAccount} onClick={() => onChange(p.id, p)}>
                  <CardContent sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', py: 1.5 }}>
                    {selected ? <CheckCircle sx={{ color: brand.primary, mt: 0.25 }} /> : <RadioButtonUnchecked sx={{ color: text.tertiary, mt: 0.25 }} />}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 600 }}>
                        {p.surname}{p.first_names ? `, ${p.first_names}` : ''}
                      </Typography>
                      <Typography variant="body2" sx={{ color: text.secondary }}>
                        {[p.firm, p.town].filter(Boolean).join(' · ')}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 0.75 }} useFlexGap flexWrap="wrap">
                        <Chip size="small" label="Conveyancer" color="success" variant="outlined" />
                        {p.is_notary && <Chip size="small" label="Notary public" variant="outlined" />}
                        {takenByAnotherAccount && <Chip size="small" label="Already linked to another account" color="warning" />}
                      </Stack>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
